import { useState, useEffect } from "react";
import { LoadingScreen, NoAccessScreen } from "../../components";
import { AppFeature } from "../../Constant";
import { usePagePermission } from "../../Hook";
import { FetchReportByCustomer_api, FetchCustomerData_api } from "../../Api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Download, SlidersHorizontal, RotateCcw, User } from "lucide-react";
import { DatePicker, Select, Spin, Input, Drawer, Space, Button as AntdButton } from "antd";
import { useDebounce } from "use-debounce";
import { downloadExcel } from "../../lib/excelGenerator";
import dayjs from "dayjs";

const { Option } = Select;
const { RangePicker } = DatePicker;

export const ReportByCustomer = () => {
  const { isLoading: permissionLoading, hasPermission } = usePagePermission(
    AppFeature.REPORT_BY_CUSTOMER,
    ["admin", "cashier", "manager"]
  );

  const [reportData, setReportData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [customerData, setCustomerData] = useState<any[]>([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [debouncedSearch] = useDebounce(searchKeyword, 500);

  const [customerId, setCustomerId] = useState<number | undefined>(undefined);
  const [inquiryNumber, setInquiryNumber] = useState("");
  const [engineNumber, setEngineNumber] = useState("");
  const [chassisNumber, setChassisNumber] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draftCustomerId, setDraftCustomerId] = useState<number | undefined>(undefined);
  const [draftInquiryNumber, setDraftInquiryNumber] = useState("");
  const [draftEngineNumber, setDraftEngineNumber] = useState("");
  const [draftChassisNumber, setDraftChassisNumber] = useState("");
  const [draftRange, setDraftRange] = useState<any>(null);

  const [inquiryTotal, setInquiryTotal] = useState(0);
  const [totalPaidAllTime, setTotalPaidAllTime] = useState(0);
  const [paidTotalPeriod, setPaidTotalPeriod] = useState(0);
  const [hasApplied, setHasApplied] = useState(false);

  useEffect(() => {
    const fetchCustomers = async () => {
      const response: any = await FetchCustomerData_api(debouncedSearch.trim());
      if (response && response.customerData) {
        setCustomerData(response.customerData);
      }
    };
    fetchCustomers();
  }, [debouncedSearch]);

  const fetchReport = async (
    custId?: number,
    start: string = "",
    end: string = "",
    inqNo: string = "",
    engNo: string = "",
    chasNo: string = "",
  ) => {
    setLoading(true);
    const response = await FetchReportByCustomer_api(
      custId || 0,
      start,
      end,
      inqNo,
      engNo,
      chasNo,
    );
    if (response.success) {
      setReportData(response.data);
      setInquiryTotal(Number(response.inquiryTotal) || 0);
      setTotalPaidAllTime(Number(response.totalPaidAllTime) || 0);
      setPaidTotalPeriod(Number(response.totalPaidPeriod) || 0);
    } else {
      alert(response.msg || "Failed to fetch report");
    }
    setLoading(false);
  };

  const openDrawer = () => {
    setDraftCustomerId(customerId);
    setDraftInquiryNumber(inquiryNumber);
    setDraftEngineNumber(engineNumber);
    setDraftChassisNumber(chassisNumber);
    setDraftRange(
      startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null,
    );
    setDrawerOpen(true);
  };

  const applyFilters = () => {
    setCustomerId(draftCustomerId);
    setInquiryNumber(draftInquiryNumber.trim());
    setEngineNumber(draftEngineNumber.trim());
    setChassisNumber(draftChassisNumber.trim());
    setStartDate(draftRange?.[0] ? draftRange[0].format("YYYY-MM-DD") : "");
    setEndDate(draftRange?.[1] ? draftRange[1].format("YYYY-MM-DD") : "");
    setHasApplied(true);
    setDrawerOpen(false);
    fetchReport(
      draftCustomerId,
      draftRange?.[0] ? draftRange[0].format("YYYY-MM-DD") : "",
      draftRange?.[1] ? draftRange[1].format("YYYY-MM-DD") : "",
      draftInquiryNumber.trim(),
      draftEngineNumber.trim(),
      draftChassisNumber.trim(),
    );
  };

  const resetFilters = () => {
    setDraftCustomerId(undefined);
    setDraftInquiryNumber("");
    setDraftEngineNumber("");
    setDraftChassisNumber("");
    setDraftRange(null);
    setCustomerId(undefined);
    setInquiryNumber("");
    setEngineNumber("");
    setChassisNumber("");
    setStartDate("");
    setEndDate("");
    setHasApplied(false);
    setReportData([]);
    setPaidTotalPeriod(0);
    setDrawerOpen(false);
  };

  const toNumber = (value: any): number =>
    Number(String(value ?? "0").replace(/,/g, "")) || 0;

  const calculatePaidTotalValue = () => {
    return toNumber(paidTotalPeriod);
  };

  const calculateDueTotalValue = () => {
    return inquiryTotal - totalPaidAllTime;
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-LK", {
      style: "currency",
      currency: "LKR",
      minimumFractionDigits: 2,
    }).format(value);

  const isPeriodActive = !!startDate && !!endDate;
  const activeCustomerName = customerData.find((c) => c.id === customerId)?.name || "";
  const filterSummary = [
    activeCustomerName,
    inquiryNumber ? `Inquiry: ${inquiryNumber}` : "",
    engineNumber ? `Engine: ${engineNumber}` : "",
    chassisNumber ? `Chassis: ${chassisNumber}` : "",
    isPeriodActive ? `${startDate} to ${endDate}` : "",
  ].filter(Boolean).join(" | ");

  const handleDownloadExcel = () => {
    const headers = [
      "Date",
      "Customer",
      "Mobile",
      "Inquiry Reference",
      "Slip ID",
      "Method",
      "Engine No",
      "Chassis No",
      "Amount",
    ];
    const data = reportData.map((row) => [
      row.slip_date,
      row.customer_name,
      row.customer_mobile || "N/A",
      row.inquiryNumber || "N/A",
      row.slip_id,
      row.payment_method,
      row.engine_number || "N/A",
      row.chassis_number || "N/A",
      toNumber(row.amount),
    ]);
    const title = `Customer Payment Report${filterSummary ? ` (${filterSummary})` : ""}`;
    downloadExcel(
      title,
      headers,
      data,
      `${title}`,
      formatCurrency(calculatePaidTotalValue()),
      formatCurrency(calculateDueTotalValue()),
      formatCurrency(inquiryTotal),
    );
  };

  if (permissionLoading) {
    return <LoadingScreen messageQueue={"Loading..."} />;
  }

  if (!hasPermission) {
    return <NoAccessScreen />;
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center px-6 pb-5">
        <h1 className="text-2xl font-semibold">Payment Report by Customer</h1>
        <div className="flex gap-3">
          <Button
            onClick={openDrawer}
            className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white border-none shadow-sm px-4 h-10 rounded-lg transition-all"
          >
            <SlidersHorizontal className="w-4 h-4 text-white" />
            <span className="font-medium text-sm">Filters</span>
          </Button>
          <Button
            onClick={handleDownloadExcel}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white border-none shadow-sm px-4 h-10 rounded-lg transition-all"
            disabled={reportData.length === 0}
          >
            <Download className="w-4 h-4 text-white" />
            <span className="font-medium text-sm">Excel</span>
          </Button>
        </div>
      </div>

      <div className="bg-white mx-6 mb-6 p-4 rounded-xl shadow-sm">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <User className="w-4 h-4 text-emerald-600" />
          <span>Filters: </span>
          <span className="font-medium text-gray-700">
            {filterSummary || "None applied — use Filters to search payments by customer, inquiry or vehicle"}
          </span>
        </div>
      </div>

      {reportData.length > 0 && (
        <div className="px-6 pb-6">
          {/* Green Summary Card */}
          <div className="bg-emerald-600 rounded-xl shadow-md overflow-hidden mb-6 p-6 text-white text-sm">
            <div className="flex flex-wrap gap-12">
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">TOTAL INQUIRY AMOUNT</p>
                <p className="text-2xl font-bold">{formatCurrency(inquiryTotal)}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">TOTAL AMOUNT PAID{isPeriodActive && <span className="normal-case tracking-normal"> (this period)</span>}</p>
                <p className="text-2xl font-bold">{formatCurrency(calculatePaidTotalValue())}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">TOTAL DUE AMOUNT</p>
                <p className="text-2xl font-bold">{formatCurrency(calculateDueTotalValue())}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col">
            <div className="flex-grow overflow-auto max-h-[calc(100vh-320px)]">
              {loading ? (
                <div className="flex justify-center items-center py-20">
                  <Spin size="large" />
                </div>
              ) : (
                <Table className="min-w-max text-sm">
                  <TableHeader className="bg-gray-100/50">
                    <TableRow className="border-b">
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Date</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Customer</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Mobile</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Inquiry Reference</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Transaction ID</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Method</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Engine No</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Chassis No</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.map((row, index) => (
                      <TableRow
                        key={`${row.id}-${index}`}
                        className="border-b last:border-0 hover:bg-gray-50"
                      >
                        <TableCell className="px-4 py-3">{row.slip_date}</TableCell>
                        <TableCell className="px-4 py-3">{row.customer_name}</TableCell>
                        <TableCell className="px-4 py-3 font-mono text-xs">{row.customer_mobile || "N/A"}</TableCell>
                        <TableCell className="px-4 py-3">{row.inquiryNumber || "N/A"}</TableCell>
                        <TableCell className="px-4 py-3 text-gray-500 font-mono text-xs">{row.slip_id}</TableCell>
                        <TableCell className="px-4 py-3 capitalize">{row.payment_method}</TableCell>
                        <TableCell className="px-4 py-3 font-mono text-xs">{row.engine_number || "N/A"}</TableCell>
                        <TableCell className="px-4 py-3 font-mono text-xs">{row.chassis_number || "N/A"}</TableCell>
                        <TableCell className="px-4 py-3 text-right font-medium">
                          {formatCurrency(toNumber(row.amount))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </div>
      )}

      {!loading && reportData.length === 0 && hasApplied && (
        <div className="px-6 pb-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col items-center justify-center py-20 text-gray-500">
            <p>No payments match the applied filters.</p>
          </div>
        </div>
      )}
      {!loading && reportData.length === 0 && !hasApplied && (
        <div className="px-6 pb-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col items-center justify-center py-20 text-gray-500">
            <p>Apply filters to search payments by customer, inquiry number or vehicle.</p>
          </div>
        </div>
      )}

      <Drawer
        title="Payment Report Filters"
        placement="right"
        width={400}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        footer={
          <div className="flex justify-end gap-3">
            <AntdButton
              onClick={resetFilters}
              className="flex items-center gap-2 border-gray-200"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </AntdButton>
            <AntdButton
              type="primary"
              onClick={applyFilters}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Apply Filters
            </AntdButton>
          </div>
        }
      >
        <Space direction="vertical" size="large" className="w-full">
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Customer
            </label>
            <Select
              showSearch
              size="large"
              placeholder="All Customers"
              className="w-full premium-select"
              onSearch={(val) => setSearchKeyword(val)}
              onChange={(val) => setDraftCustomerId(val)}
              value={draftCustomerId}
              allowClear
              filterOption={false}
              notFoundContent={loading ? <Spin size="small" /> : <div className="p-4 text-gray-400 text-sm text-center">No customers found</div>}
            >
              {customerData.map((c) => (
                <Option key={c.id} value={c.id}>
                  <div className="flex justify-between items-center py-1">
                    <span className="font-semibold text-gray-800">{c.name}</span>
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-mono">{c.mobile}</span>
                  </div>
                </Option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Inquiry Number
            </label>
            <Input
              size="large"
              placeholder="e.g. PNQNLY2026072407447"
              value={draftInquiryNumber}
              onChange={(e) => setDraftInquiryNumber(e.target.value)}
              allowClear
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Engine Number
            </label>
            <Input
              size="large"
              placeholder="e.g. PDXCTB80431"
              value={draftEngineNumber}
              onChange={(e) => setDraftEngineNumber(e.target.value)}
              allowClear
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Chassis Number
            </label>
            <Input
              size="large"
              placeholder="e.g. MD2B54DX5TCB64336"
              value={draftChassisNumber}
              onChange={(e) => setDraftChassisNumber(e.target.value)}
              allowClear
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Date Range
            </label>
            <RangePicker
              value={draftRange}
              onChange={(dates) => setDraftRange(dates)}
              className="w-full"
            />
          </div>
        </Space>
      </Drawer>
    </div>
  );
};